import DownloadIcon from '@mui/icons-material/Download';
import InfoIcon from '@mui/icons-material/Info';
import RefreshIcon from '@mui/icons-material/Refresh';
import { Alert, AlertTitle, Button, Link, Stack, Tooltip, Typography, useEventCallback, useTheme } from '@mui/material';
import { AbsReactTable, TABLE } from 'customer_portal-ui-shared';
import { format } from 'date-fns';
import { useEffect, useRef, useState } from 'react';
import { RiskLookup } from '../../constants';
import { IPSCRow } from '../../contracts/psc-row.contract';
import { ChecklistFetch } from '../../features/checklist.feature';
import { VesselFetch } from '../../features/vessel.feature';
import { useAppDispatch, useAppSelector } from '../../hooks';
import { ConfigService } from '../../services';
import PscAisDestination from './psc-ais-destination.component';
import PscAisEta from './psc-ais-eta.component';
import PSCChecklist from './psc-checklistpdf-component';
import getCheckListPDF from './psc-general-checklist.pdf';
import getCICChecklistPDF from './CIC_Checklist_Final.pdf';
import PSCInspectionRiskLabel from './psc-inspection-risk-label.component';
import PscInspectionRiskMonth from './psc-inspection-risk-month.component';
import PscInspectionRisk from './psc-inspection-risk.component';
import PscMouHeader from './psc-mou-header.component';
import PscMou from './psc-mou.component';
import SubscribeButton from './psc-subscribe-button.component';
import PscVesselRisk from './psc-vessel-risk.component';

const PscList = () => {
    const dispatch = useAppDispatch();

    const vessels = useAppSelector((state) => state.Vessel.List.Vessels);
    const isLoading = useAppSelector((state) => state.Vessel.List.IsLoading);
    const vesselRiskData = useAppSelector((state) => state.MOURisk.Selected);
    const checklistState = useAppSelector((state) => state.Checklist);
    const aisData = useAppSelector((state) => state.Ais.List);

    const month1Ref = useRef(null);
    const checkListRef = useRef(null);

    const theme = useTheme();
    const [pscData, setPSCData] = useState<IPSCRow[]>([]);
    const [filterData, setFilterData] = useState<IPSCRow[]>([]);

    const formatEta = (eta: string | Date | undefined): string => {
        if (!eta) return '';
        return typeof eta === 'string' ? eta : format(eta, 'yyyy-MM-dd HH:mm:ss');
    };

    const initialPSCHeaderState = [
        {
            label: 'Vessel Name',
            accessor: 'VesselName',
            Cell: ({ row }: { row: any }) => {
                return (
                    <>
                        <strong>{row.original.VesselName}</strong>
                        <Typography variant="caption" display="block">
                            {row.original.VesselType}
                        </Typography>
                    </>
                );
            },
            sorting: true,
            filtering: true
        },
        {
            Header: <center>IMO Number</center>,
            label: 'IMO Number',
            accessor: 'ImoNumber',
            Cell: ({ row }: { row: any }) => <center>{row.original.ImoNumber}</center>,
            sorting: true,
            filtering: true
        },
        {
            Header: <center>Class Number</center>,
            label: 'Class Number',
            accessor: 'ClassNumber',
            Cell: ({ row }: { row: any }) => <center>{row.original.ClassNumber}</center>,
            sorting: true,
            filtering: true
        },
        {
            Header: <center>AIS ETA</center>,
            label: 'AIS ETA',
            accessor: 'Eta',
            dataType: 'date',
            Cell: ({ row }: { row: any }) => <PscAisEta imoNumber={row.original.ImoNumber} />,
            sorting: false,
            filtering: false
        },
        {
            Header: (
                <Tooltip
                    title={
                        <Alert severity="info">
                            <AlertTitle>AIS Disclaimer</AlertTitle>
                            <span>
                                The first line is the destination information as entered by the vessel’s crew into the AIS to be reported. <br />
                                The second line is the Associated Destination as determined by the Regulatory World Map.
                            </span>
                        </Alert>
                    }
                    placement="left"
                    arrow
                >
                    <span>
                        AIS Destination Port &nbsp;
                        <InfoIcon />
                    </span>
                </Tooltip>
            ),
            label: 'AIS Destination',
            accessor: 'Destination',
            Cell: ({ row }: { row: any }) => <PscAisDestination imoNumber={row.original.ImoNumber} />,
            sorting: false,
            filtering: false
        },
        {
            Header: <PscMouHeader postChange={useEventCallback(() => PopulatePSCTable())} />,
            label: 'MOU',
            accessor: 'MOU',
            Cell: ({ row }: { row: any }) => <PscMou imoNumber={row.original.ImoNumber} />,
            sorting: false,
            filtering: false
        },
        {
            label: (
                <Tooltip
                    title={
                        <Alert severity="info">
                            <AlertTitle>Vessel Risk</AlertTitle>
                            <span>Risk is determined by PSC Regime.</span>
                        </Alert>
                    }
                    placement="left"
                    arrow
                >
                    <Stack direction={'row'} gap={1}>
                        Vessel Risk
                        <InfoIcon />
                    </Stack>
                </Tooltip>
            ),
            accessor: 'VesselRisk',
            dataType: 'highlow',
            isOnlyCustomValues: true,
            customValues: {
                data: [
                    {
                        label: RiskLookup.High.Text,
                        value: RiskLookup.High.Value,
                        selected: true
                    },
                    {
                        label: RiskLookup.Medium.Text,
                        value: RiskLookup.Medium.Value,
                        selected: true
                    },
                    {
                        label: RiskLookup.Low.Text,
                        value: RiskLookup.Low.Value,
                        selected: true
                    },
                    {
                        label: RiskLookup.NotYetVisited.Text,
                        value: RiskLookup.NotYetVisited.Value,
                        selected: true
                    },
                    {
                        label: RiskLookup.NoData.Text,
                        value: RiskLookup.NoData.Value,
                        selected: true
                    }
                ]
            },
            Cell: ({ row }: { row: any }) => <PscVesselRisk imoNumber={row.original.ImoNumber} />,
            sorting: true,
            filtering: true,
            allowOrdering: false,
            allowShowHide: false
        },
        {
            label: (
                <Stack ref={month1Ref}>
                    <PscInspectionRiskMonth monthIndex={0} />
                </Stack>
            ),
            allowOrdering: false,
            allowShowHide: false,
            accessor: 'Month1',
            dataType: 'highlow',
            isOnlyCustomValues: true,
            customValues: {
                data: [
                    {
                        label: RiskLookup.High.Text,
                        value: RiskLookup.High.Value,
                        selected: true
                    },
                    {
                        label: RiskLookup.Medium.Text,
                        value: RiskLookup.Medium.Value,
                        selected: true
                    },
                    {
                        label: RiskLookup.Low.Text,
                        value: RiskLookup.Low.Value,
                        selected: true
                    },
                    {
                        label: RiskLookup.NoData.Text,
                        value: RiskLookup.NoData.Value,
                        selected: true
                    }
                ]
            },
            Cell: ({ row }: { row: any }) => <PscInspectionRisk imoNumber={row.original.ImoNumber} index="0" />,
            sorting: true,
            filtering: true
        },
        {
            label: <PscInspectionRiskMonth monthIndex={1} />,
            allowOrdering: false,
            allowShowHide: false,
            accessor: 'Month2',
            dataType: 'string',
            isOnlyCustomValues: true,
            Cell: ({ row }: { row: any }) => <PscInspectionRisk imoNumber={row.original.ImoNumber} index="1" />,
            sorting: false,
            filtering: false
        },
        {
            label: <PscInspectionRiskMonth monthIndex={2} />,
            allowOrdering: false,
            allowShowHide: false,
            accessor: 'Month3',
            Cell: ({ row }: { row: any }) => <PscInspectionRisk imoNumber={row.original.ImoNumber} index="2" />,
            sorting: false,
            filtering: false
        },
        {
            label: (
                <Stack direction={'row'} ref={checkListRef}>
                    <div>PSC Checklist</div>
                </Stack>
            ),
            allowOrdering: false,
            allowShowHide: false,
            accessor: 'Checklist',
            Cell: ({ row }: { row: any }) => {
                if (ConfigService.appConfig.enableCustomChecklist)
                    return (
                        <PSCChecklist
                            imoNumber={row.original.ImoNumber}
                            port="Akitafunagawa"
                            classNumber={row.original.ClassNumber}
                            eta={formatEta(aisData[row.original.ImoNumber]?.AIS?.Eta)}
                            vesselName={row.original.VesselName}
                            generateChecklistPdf={generateChecklistPdf}
                        />
                    );

                return (
                    <center>
                        <Link href={getCheckListPDF} target="_blank" color="#FFFFFF">
                            <Button variant="outlined" style={{ border: 0 }}>
                                <DownloadIcon />
                            </Button>
                        </Link>
                    </center>
                );
            },
            sorting: false,
            filtering: false
        }
    ];
    const [pscHeader] = useState<any[]>([...initialPSCHeaderState]);

    const tablePage = [10, 20, 50, 100];
    const initialFilterState = {
        resetPageIndex: true,
        expandAll: true,
        resetFilters: true,
        deselectRows: true,
        pageIndex: 0,
        pageSize: tablePage[0],
        searchType: 'list'
    };
    const [appliedFilters, setAppliedFilters] = useState({
        ...initialFilterState
    });

    useEffect(() => {
        dispatch(VesselFetch.GetByClient());
    }, [dispatch]);

    useEffect(() => {
        PopulatePSCTable();
    }, [vessels]);

    const PopulatePSCTable = () => {
        if (!vessels || vessels.length === 0) return;

        const sortedVessels = [...vessels].sort((a, b) => a.VesselName.localeCompare(b.VesselName));
        const rows: IPSCRow[] = sortedVessels.map((vessel) => {
            return {
                ImoNumber: vessel.ImoNumber,
                VesselName: vessel.VesselName,
                VesselType: vessel.VesselType,
                ClassNumber: vessel.ClassNumber,
                Destination: undefined,
                Eta: undefined,
                MOU: undefined,
                VesselRisk: undefined,
                Month1: undefined,
                Month2: undefined,
                Month3: undefined,
                Checklist: undefined
            };
        });

        setPSCData(rows);
    };

    const applyFilter = useEventCallback((filtersObj: any) => {
        const updatedFilters = {
            ...filtersObj
        };

        let filteredData = pscData;

        // Sorting Logic for Vessel Risk
        if (updatedFilters.sort && updatedFilters.sort.columnId && updatedFilters.sort?.columnId === 'VesselRisk') {
            const sortAscending: boolean = updatedFilters.sort.sortOrder === 'asc';

            filteredData.forEach((row) => {
                const riskId = vesselRiskData[row.ImoNumber]?.RiskId;

                let sortOrder = 0;
                if (riskId === undefined) sortOrder = 0;
                else if (riskId === RiskLookup.High.Value) sortOrder = RiskLookup.High.SortOrder;
                else if (riskId === RiskLookup.Medium.Value) sortOrder = RiskLookup.Medium.SortOrder;
                else if (riskId === RiskLookup.Low.Value) sortOrder = RiskLookup.Low.SortOrder;
                else if (riskId === RiskLookup.NotYetVisited.Value) sortOrder = RiskLookup.NotYetVisited.SortOrder;

                row.VesselRisk = sortOrder;
            });

            if (sortAscending) {
                filteredData.sort((a, b) => a.VesselRisk - b.VesselRisk);
            } else {
                filteredData.sort((a, b) => b.VesselRisk - a.VesselRisk);
            }
        }

        // Sorting Logic for Month1
        if (updatedFilters.sort && updatedFilters.sort.columnId && updatedFilters.sort?.columnId === 'Month1') {
            const sortAscending: boolean = updatedFilters.sort.sortOrder === 'asc';

            filteredData.forEach((row) => {
                const riskId = vesselRiskData[row.ImoNumber]?.InspectionWindow[0]?.RiskId;

                let sortOrder = 0;
                if (riskId === undefined) sortOrder = 0;
                else if (riskId === RiskLookup.High.Value) sortOrder = RiskLookup.High.SortOrder;
                else if (riskId === RiskLookup.Medium.Value) sortOrder = RiskLookup.Medium.SortOrder;
                else if (riskId === RiskLookup.Low.Value) sortOrder = RiskLookup.Low.SortOrder;
                else if (riskId === RiskLookup.NotYetVisited.Value) sortOrder = RiskLookup.NotYetVisited.SortOrder;

                row.Month1 = sortOrder;
            });

            if (sortAscending) {
                filteredData.sort((a, b) => a.Month1 - b.Month1);
            } else {
                filteredData.sort((a, b) => b.Month1 - a.Month1);
            }
        }

        // Filter logic for Vessel Risk
        if (updatedFilters.filters && updatedFilters.filters.VesselRisk && updatedFilters.filters.VesselRisk.columnId === 'VesselRisk') {
            filteredData.forEach((row) => {
                const riskId = vesselRiskData[row.ImoNumber]?.RiskId;

                let VesselRiskData = '';
                if (riskId === undefined) VesselRiskData = '-1';
                else if (riskId === RiskLookup.High.Value) VesselRiskData = RiskLookup.High.Value.toString();
                else if (riskId === RiskLookup.Medium.Value) VesselRiskData = RiskLookup.Medium.Value.toString();
                else if (riskId === RiskLookup.Low.Value) VesselRiskData = RiskLookup.Low.Value.toString();
                else if (riskId === RiskLookup.NotYetVisited.Value) VesselRiskData = RiskLookup.NotYetVisited.Value.toString();

                row.VesselRisk = VesselRiskData;
            });

            filteredData = filteredData.filter((row) => updatedFilters.filters.VesselRisk.selectedFilters.includes(row.VesselRisk));
        }

        // Filter logic for Month1
        if (updatedFilters.filters && updatedFilters.filters.Month1 && updatedFilters.filters.Month1.columnId === 'Month1') {
            filteredData.forEach((row) => {
                const riskId = vesselRiskData[row.ImoNumber]?.InspectionWindow[0]?.RiskId;

                let Month1data = '';
                if (riskId === undefined) Month1data = '-1';
                else if (riskId === RiskLookup.High.Value) Month1data = RiskLookup.High.Value.toString();
                else if (riskId === RiskLookup.Medium.Value) Month1data = RiskLookup.Medium.Value.toString();
                else if (riskId === RiskLookup.Low.Value) Month1data = RiskLookup.Low.Value.toString();
                else if (riskId === RiskLookup.NotYetVisited.Value) Month1data = RiskLookup.NotYetVisited.Value.toString();

                row.Month1 = Month1data;
            });
            filteredData = filteredData.filter((row) => updatedFilters.filters.Month1.selectedFilters.includes(row.Month1));
        }

        // Filter Vessel Name
        if (updatedFilters.filters && updatedFilters.filters.VesselName && updatedFilters.filters.VesselName.columnId === 'VesselName') {
            filteredData = filteredData.filter((row) => updatedFilters.filters.VesselName.selectedFilters.some((filter: string) => row.VesselName.toLowerCase().includes(filter.toLowerCase())));
        }

        // Filter IMO Number
        if (updatedFilters.filters && updatedFilters.filters.ImoNumber && updatedFilters.filters.ImoNumber.columnId === 'ImoNumber') {
            filteredData = filteredData.filter((row) => updatedFilters.filters.ImoNumber.selectedFilters.some((filter: string) => row.ImoNumber.toLowerCase().includes(filter.toLowerCase())));
        }

        // Filter Class Number
        if (updatedFilters.filters && updatedFilters.filters.ClassNumber && updatedFilters.filters.ClassNumber.columnId === 'ClassNumber') {
            filteredData = filteredData.filter((row) => updatedFilters.filters.ClassNumber.selectedFilters.some((filter: string) => row.ClassNumber.toLowerCase().includes(filter.toLowerCase())));
        }

        setFilterData(filteredData);
        setAppliedFilters(updatedFilters);
    });

    const generateChecklistPdf = async (imoNumber: string, port: string, classNumber: string, eta: string, vesselName: string) => {
        try {
            console.log(`Generating checklist PDF for IMO number: ${imoNumber}, port: ${port}, class number: ${classNumber}, ETA: ${eta}, vessel name: ${vesselName}`);
            const resultAction = await dispatch(ChecklistFetch.GenerateChecklistPdf({ imoNumber, port, classNumber, eta, vesselName }));

            if (ChecklistFetch.GenerateChecklistPdf.fulfilled.match(resultAction)) {
                const { pdfBlob } = resultAction.payload;
                const pdfUrl = URL.createObjectURL(pdfBlob);
                const link = document.createElement('a');
                link.href = pdfUrl;
                link.download = `checklist_${imoNumber}.pdf`;
                document.body.appendChild(link);
                link.click();
                document.body.removeChild(link);
                URL.revokeObjectURL(pdfUrl);
                console.log('Checklist PDF generated and downloaded successfully');
            } else {
                console.error('Error generating checklist PDF:', resultAction.payload);
            }
        } catch (error) {
            console.error('Error generating checklist PDF:', error);
        }
    };

    const downloadCICChecklist = () => {
        const link = document.createElement('a');
        link.href = getCICChecklistPDF;
        link.download = 'CIC_Checklist.pdf';
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
    };

    useEffect(() => {
        if (checklistState) {
            Object.keys(checklistState).forEach((imoNumber) => {
                const checklist = checklistState[imoNumber];
                if (checklist && checklist.PdfBlob) {
                    const pdfUrl = URL.createObjectURL(checklist.PdfBlob);
                    window.open(pdfUrl);
                }
            });
        }
    }, [checklistState]);

    return (
        <Stack direction={'column'} spacing={0}>
            <Stack direction={'row'} spacing={2}>
                <Button startIcon={<RefreshIcon />} onClick={() => window.location.reload()}>
                    Refresh Table Data
                </Button>
                {ConfigService.appConfig.enableSubscription && <SubscribeButton />}
                <Tooltip
                    title={
                        <Alert severity="info">
                            <AlertTitle>Paris/Tokyo MoU CIC Checklist</AlertTitle>
                            <span>
                                Paris and Tokyo MOUs Concentrated Inspection Campaign (CIC) will be on "Cargo Securing of Cargo Units and Cargo Trasport Units" from September 1st to November 30th. The
                                relevant checklist can be download here
                            </span>
                        </Alert>
                    }
                    placement="right"
                    arrow
                    sx={{
                        '& .MuiTooltip-tooltip': {
                            backgroundColor: theme.palette.background.paper,
                            color: theme.palette.text.primary,
                            boxShadow: theme.shadows[1],
                            fontSize: '0.875rem'
                        }
                    }}
                >
                    <Button variant="contained" color="secondary" onClick={downloadCICChecklist} sx={{ height: '36px' }}>
                        Paris/Tokyo MoU CIC Checklist
                    </Button>
                </Tooltip>

                <PSCInspectionRiskLabel month1Ref={month1Ref} checkListRef={checkListRef} heightSize={40} />
            </Stack>
            <AbsReactTable
                loading={isLoading}
                tableType={TABLE.TYPE.DATA}
                columns={pscHeader}
                data={filterData.length > 0 ? filterData : pscData}
                dataCount={filterData.length > 0 ? filterData.length : pscData?.length}
                pagination={false}
                showSearchTypes={false}
                applyFilterCallback={applyFilter}
                appliedFilters={appliedFilters}
                pageOptions={tablePage}
            />
        </Stack>
    );
};

export default PscList;
